import { useState, useEffect, useCallback, useRef } from "react";
import type { PublicStatusResponse } from "@statuspage/shared";
import { fetchStatus } from "../lib/api";

interface UseStatusResult {
  data: PublicStatusResponse | null;
  error: string | null;
  loading: boolean;
  refetch: () => void;
}

export function useStatus(tenant: string | undefined): UseStatusResult {
  const [data, setData] = useState<PublicStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tenantRef = useRef(tenant);
  const abortRef = useRef<AbortController | null>(null);

  const fetch = useCallback(async () => {
    if (!tenant) return;

    // Abort previous in-flight request to avoid race conditions
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await fetchStatus(tenant, controller.signal);
      if (!controller.signal.aborted) {
        setData(result);
        setError(null);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const message = err instanceof Error ? err.message : "Unknown error";
      if (!controller.signal.aborted) {
        setError(message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [tenant]);

  // Initial fetch + auto-refresh every 60s
  useEffect(() => {
    tenantRef.current = tenant;
    setLoading(true);
    setError(null);
    fetch();

    intervalRef.current = setInterval(fetch, 60_000);

    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tenant, fetch]);

  // Revalidate on browser focus (visibilitychange)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetch();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [fetch]);

  return { data, error, loading, refetch: fetch };
}
